import { createContext, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification } from 'sonata-react-components';

import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { AddToGroupModal } from '../AddToGroupModal/AddToGroupModal';
import { EditGroupsModal } from '../EditGroupsModal/EditGroupsModal';
import { NewGroupModal } from '../NewGroupModal/NewGroupModal';
import { formatGroupingNotice } from './groupingNotification';
import { useItemsDeletion } from './useItemsDeletion';

import type { ReactNode } from 'react';
import type { GroupingNotice } from '../groupingNotice.types';

/** Which grouping modal to open; `edit` with a `groupId` lands on that group's detail. */
export type GroupModalRequest = { mode: 'new' } | { mode: 'add' } | { groupId?: string; mode: 'edit' };

export interface GroupingModalsValue {
  /** Deletes list items of the current family, deleting the groups they leave empty (with confirmation if used). */
  deleteItems: (itemIds: string[]) => void;
  /** Rows to highlight after a group is created (until the user selects rows again). */
  highlightedItemIds: string[];
  openGroupModal: (request: GroupModalRequest) => void;
}

const GroupingModalsContext = createContext<GroupingModalsValue | null>(null);

const NO_IDS: string[] = [];

/** Hosts the grouping modals of the current list and what happens after each change. */
export function GroupingModalsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { formatMessage } = useIntl();
  const { list: family, selectedItems, setSelectedItems } = useMultiFlightMap();
  const [request, setRequest] = useState<GroupModalRequest | null>(null);
  const [highlightedItemIds, setHighlightedItemIds] = useState<string[]>(NO_IDS);
  const selectedIds = selectedItems[family];
  const itemsDeletion = useItemsDeletion(family);

  useEffect(() => {
    if (selectedIds.length) {
      setHighlightedItemIds(NO_IDS);
    }
  }, [selectedIds]);

  useEffect(() => {
    setHighlightedItemIds(NO_IDS);
  }, [family]);

  const close = () => setRequest(null);

  const handleApplied = (notice: GroupingNotice) => {
    close();
    showNotification('success', formatGroupingNotice(formatMessage, family, notice));
    setSelectedItems(family, []);
    const created = notice.kind === 'created' || notice.kind === 'cartography';
    setHighlightedItemIds(created ? notice.affectedItemIds : NO_IDS);
  };

  const value: GroupingModalsValue = {
    deleteItems: itemsDeletion.deleteItems,
    highlightedItemIds,
    openGroupModal: setRequest
  };

  return (
    <GroupingModalsContext.Provider value={value}>
      {children}
      {request?.mode === 'new' && (
        <NewGroupModal family={family} itemIds={selectedIds} onApplied={handleApplied} onClose={close} />
      )}
      {request?.mode === 'add' && (
        <AddToGroupModal family={family} itemIds={selectedIds} onApplied={handleApplied} onClose={close} />
      )}
      {itemsDeletion.prompt}
      {request?.mode === 'edit' && (
        <EditGroupsModal family={family} initialGroupId={request.groupId} onApplied={handleApplied} onClose={close} />
      )}
    </GroupingModalsContext.Provider>
  );
}

/** Opens grouping modals and exposes the rows to highlight; must be used under `GroupingModalsProvider`. */
export function useGroupingModals(): GroupingModalsValue {
  const value = useContext(GroupingModalsContext);
  if (!value) {
    throw new Error('useGroupingModals must be used within a GroupingModalsProvider');
  }
  return value;
}
